import React from 'react';
import { LegalPageLayout } from '../../components/legal/LegalPageLayout';
import { LegalSection } from '../../components/legal/LegalSection';
import { AffiliateDisclaimer } from '../../components/legal/AffiliateDisclaimer';
import { AlertTriangle, Bot, Database, Globe } from 'lucide-react';

export function Disclaimer() {
  return (
    <LegalPageLayout title="Disclaimer">
      <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-lg mb-8">
        <div className="flex items-center gap-2 mb-3">
          <AlertTriangle className="h-6 w-6 text-red-600" />
          <h2 className="text-xl font-bold text-red-800">Critical Notice</h2>
        </div>
        <p className="text-red-700 font-medium">
          NigeriaFx is an informational platform only. We do not provide financial advice, 
          trading services, or investment recommendations. All content is for educational 
          and informational purposes only.
        </p>
      </div>

      <LegalSection title="Financial Information Disclaimer">
        <p className="mb-4">
          The information provided on NigeriaFx is for <strong>general information purposes only</strong> 
          and should not be considered as financial, investment, or trading advice of any kind. We are 
          not licensed financial advisors, investment advisors, or trading professionals.
        </p>
        <p className="mb-4">
          <strong>We do not guarantee the accuracy, completeness, timeliness, or reliability</strong> 
          of any information on this website. All data is provided "as is\" without any warranties, 
          express or implied.
        </p>
      </LegalSection>

      <LegalSection title="Data Sources and AI Content">
        <div className="space-y-4">
          <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg">
            <Database className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="font-semibold text-blue-800 mb-2">Public Domain Information</h4>
              <p className="text-blue-700 text-sm">
                Exchange rates, market data, and financial information are obtained from publicly 
                available sources, third-party APIs, financial institutions, and government databases. 
                We aggregate this information for convenience but do not verify its accuracy.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3 p-4 bg-purple-50 rounded-lg">
            <Bot className="h-5 w-5 text-purple-600 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="font-semibold text-purple-800 mb-2">AI-Generated Content</h4>
              <p className="text-purple-700 text-sm">
                Some content on this platform is automatically generated using artificial intelligence 
                based on market data, trends, and algorithms. AI-generated content is clearly marked 
                and should be independently verified before making any financial decisions.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3 p-4 bg-green-50 rounded-lg">
            <Globe className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="font-semibold text-green-800 mb-2">Third-Party Sources</h4>
              <p className="text-green-700 text-sm">
                We may display information from external sources including news agencies, 
                financial data providers, and government institutions. We are not responsible 
                for the accuracy or reliability of third-party content.
              </p>
            </div>
          </div>
        </div>
      </LegalSection>

      <LegalSection title="Exchange Rates">
        <p className="mb-4">
          Exchange rates displayed on NigeriaFx are obtained from various public sources including 
          APIs, financial institutions, and market data providers. These rates are provided for 
          <strong> informational and comparison purposes only</strong>.
        </p>
        <p className="mb-4">
          <strong>Actual market rates may vary significantly</strong> from displayed rates. You should 
          always confirm current rates with licensed financial institutions, banks, or authorized 
          foreign exchange dealers before making any transactions.
        </p>
        <p className="mb-4">
          We do not facilitate, execute, or provide any currency exchange services. We are not 
          a financial institution, money service business, or foreign exchange dealer.
        </p>
      </LegalSection>

      <LegalSection title="No Financial or Investment Advice">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <p className="text-red-700 font-medium">
            <strong>IMPORTANT:</strong> Nothing on this website constitutes professional financial, 
            investment, or trading advice.
          </p>
        </div>
        <p className="mb-4">
          All content, including but not limited to exchange rates, market analysis, educational 
          materials, and commentary, is provided for informational purposes only. You should 
          <strong> always consult with qualified, licensed financial professionals</strong> before 
          making any investment, trading, or financial decisions.
        </p>
        <p className="mb-4">
          We do not provide personalized financial advice, investment recommendations, or trading 
          signals. Any decisions you make based on information from this website are entirely 
          at your own risk and responsibility.
        </p>
      </LegalSection>

      <LegalSection title="Risk Warnings">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
          <h4 className="font-semibold text-yellow-800 mb-2">High-Risk Activity Warning</h4>
          <p className="text-yellow-700 text-sm">
            Foreign exchange trading and currency speculation involve substantial risk of loss 
            and may not be suitable for all individuals. Past performance is not indicative 
            of future results.
          </p>
        </div>
        <ul className="list-disc pl-6 mb-4 space-y-2">
          <li>Currency values can fluctuate rapidly and unpredictably</li>
          <li>You may lose some or all of your invested capital</li>
          <li>Leverage can amplify both gains and losses</li>
          <li>Market conditions can change without notice</li>
          <li>Regulatory changes may affect market access</li>
        </ul>
      </LegalSection>

      <LegalSection title="Platform Limitations">
        <p className="mb-4">
          NigeriaFx is an <strong>information-only platform</strong>. We do not:
        </p>
        <ul className="list-disc pl-6 mb-4 space-y-1">
          <li>Provide financial or investment advice</li>
          <li>Execute or facilitate currency exchanges</li>
          <li>Hold, store, or transfer funds</li>
          <li>Guarantee the accuracy of displayed information</li>
          <li>Endorse any particular trading strategy or service</li>
          <li>Act as a financial institution or money service business</li>
        </ul>
      </LegalSection>

      <LegalSection title="Third-Party Content">
        <p className="mb-4">
          NigeriaFx may include content from third-party sources including news agencies, 
          financial data providers, APIs, and external websites. We are not responsible for 
          the accuracy, reliability, completeness, or availability of such content.
        </p>
        <p className="mb-4">
          <strong>Any reliance you place on third-party information is strictly at your own risk.</strong> 
          We do not endorse, verify, or guarantee any third-party content, products, or services 
          mentioned on this platform.
        </p>
      </LegalSection>

      <LegalSection title="AI-Generated Content Notice">
        <div className="flex items-start gap-3 p-4 bg-purple-50 rounded-lg mb-4">
          <Bot className="h-5 w-5 text-purple-600 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="font-semibold text-purple-800 mb-2">Artificial Intelligence Disclosure</h4>
            <p className="text-purple-700 text-sm">
              Some content on this platform is generated using artificial intelligence and 
              machine learning algorithms based on market data and trends.
            </p>
          </div>
        </div>
        <p className="mb-4">
          AI-generated content is clearly marked and includes but is not limited to:
        </p>
        <ul className="list-disc pl-6 mb-4 space-y-1">
          <li>Market analysis and commentary</li>
          <li>Daily and weekly rate summaries</li>
          <li>Educational articles and guides</li>
          <li>Breaking news alerts and updates</li>
        </ul>
        <p className="mb-4">
          <strong>AI-generated content should not be relied upon for financial decisions</strong> 
          and must be independently verified through official sources and qualified professionals.
        </p>
      </LegalSection>

      <LegalSection title="User Responsibility">
        <p className="mb-4">
          By using this platform, you acknowledge and agree that:
        </p>
        <ul className="list-disc pl-6 mb-4 space-y-2">
          <li>You will verify all information through official channels before acting</li>
          <li>You will consult with licensed professionals for financial advice</li>
          <li>You understand the risks involved in forex trading and currency exchange</li>
          <li>You will only use licensed and regulated service providers</li>
          <li>You will not rely solely on information from this platform for financial decisions</li>
          <li>You accept full responsibility for any actions taken based on platform content</li>
        </ul>
      </LegalSection>

      <LegalSection title="Service Availability">
        <p className="mb-4">
          We do not guarantee uninterrupted access to our service. The website may be unavailable 
          from time to time for maintenance, updates, or technical issues. We reserve the right 
          to modify, suspend, or discontinue any feature, service, or content at any time without notice.
        </p>
        <p className="mb-4">
          Information may become outdated, inaccurate, or incomplete. We are not obligated to 
          update or correct any information on the platform.
        </p>
      </LegalSection>

      <LegalSection title="Limitation of Liability">
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
          <p className="text-gray-700 font-medium">
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, NIGERIAFX SHALL NOT BE LIABLE FOR ANY 
            DIRECT, INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING 
            FROM YOUR USE OF THIS PLATFORM OR RELIANCE ON ANY INFORMATION PROVIDED.
          </p>
        </div>
        <p className="mb-4">
          This includes but is not limited to financial losses, trading losses, missed 
          opportunities, data loss, or any other damages resulting from the use of or 
          inability to use this platform.
        </p>
      </LegalSection>

      <AffiliateDisclaimer />
    </LegalPageLayout>
  );
}